import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { AQUAMARINE, BACKGROUND_SECONDARY } from '../../../theme';
import LoadingPanel from '../../LoadingPanel';
import Text from '../../reusable/Text';

export interface PreviewableExercise {
  video?: string | null;
  thumbnail?: string | null;
  displayName?: string | null;
}

const PreviewContainer = styled.div`
  position: relative;
  height: 62px;
  aspect-ratio: 14/9;
  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 8px;
  overflow: hidden;
`;

const PreviewImage = styled.img`
  height: 100%;
  object-fit: cover;
  opacity: .85;
`;

const PreviewVideo = styled.video`
  height: 100%;
  object-fit: cover;
`;

const LoadingContainer = styled.div`
  position: absolute;
`;

interface PreviewVideoPlayerProps {
  src: string;
  visible: boolean;
  setIsVideoLoading: (loading: boolean) => void;
}

const PreviewVideoPlayer: React.FC<PreviewVideoPlayerProps> = ({
  src,
  visible,
  setIsVideoLoading,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  // prevent a first paint flicker from stale loading state
  useLayoutEffect(() => {
    setIsVideoLoading(true);
  }, [setIsVideoLoading]);

  useEffect(() => {
    const video = videoRef.current;
    // Manually tearing down video component on unmount to avoid hitting limits on older devices before garbage collection kicks in
    return () => {
      if (video) {
        video.pause();
        video.removeAttribute('src');
        video.load();
      }
    };
  }, []);

  return (
    <PreviewVideo
      ref={videoRef}
      data-testid="exercise-preview-video"
      style={{ display: visible ? 'block' : 'none' }}
      src={src}
      autoPlay
      loop
      muted
      onLoadStart={() => setIsVideoLoading(true)}
      onCanPlay={() => setIsVideoLoading(false)}
      onError={() => setIsVideoLoading(false)}
      onPlaying={(e) => ((e.target as HTMLVideoElement).playbackRate = 3)}
      playsInline
    />
  );
};

interface ExercisePreviewProps {
  exercise: PreviewableExercise | null;
  playing?: boolean;
}

export const ExercisePreview: React.FC<ExercisePreviewProps> = ({
  exercise,
  playing,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVideoLoading, setIsVideoLoading] = useState(
    Boolean(exercise?.video),
  );

  if (!exercise) {
    return null;
  }

  if (!exercise.video && !exercise.thumbnail) {
    return (
      <div
        style={{
          padding: '8px 12px',
          borderRadius: '8px',
          background: BACKGROUND_SECONDARY,
        }}
      >
        <Text>No preview available</Text>
      </div>
    );
  }

  let state: 'loading' | 'playing' | 'paused' = 'paused';

  if (playing) {
    state = 'playing';
  }
  if (playing && isVideoLoading) {
    state = 'loading';
  }

  return (
    <PreviewContainer ref={ref} data-testid="exercise-preview">
      {exercise.thumbnail && (
        <PreviewImage
          data-testid="exercise-preview-thumbnail"
          style={{
            display: state === 'playing' ? 'none' : 'block',
          }}
          src={exercise.thumbnail}
          alt={`${exercise.displayName} exercise thumbnail`}
        />
      )}
      {exercise.video && playing && (
        <PreviewVideoPlayer
          src={exercise.video}
          visible={state === 'playing'}
          setIsVideoLoading={setIsVideoLoading}
        />
      )}
      {state === 'loading' && (
        <LoadingContainer>
          <LoadingPanel
            backgroundColor="transparent"
            iconColor={AQUAMARINE}
            noBorder
          />
        </LoadingContainer>
      )}
    </PreviewContainer>
  );
};
